import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import { type CitationParts, tidyParts } from "@/lib/tool-kit/citation-parts";
import {
  get,
  italic,
  type LegalValues,
  plain,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * Canadian cases, statutes and regulations in the McGill Guide (Canadian Guide to
 * Uniform Legal Citation, 10th edition, Thomson Reuters/Carswell, released June 2023).
 *
 * The Guide itself is a paid book, so the rules below come from the university library
 * guides that quote it chapter by chapter, read in October 2026: Queen's University
 * Library "Legal Citation with the 10th edition of the McGill Guide" (cases by pattern,
 * statutes, regulations, case history), the University of Calgary Library's McGill Guide
 * quick reference (legislation, neutral citations and parallel citations), Ontario Tech
 * University and Thompson Rivers University's McGill Guide pages (footnotes, short forms,
 * ibid and supra), and the Wikipedia summary of Canadian statute citation for the
 * volume abbreviations. Where a point rests on a single guide it is marked below.
 * - 3.1-3.2 / Pattern 1: style of cause in italics, neutral citation as the court gives
 *   it ("2012 ONCA 321"), pinpoint "at para 2", judge after the pinpoint ("LaForme JA"),
 *   short form in square brackets, the brackets roman and the name italic ("[Radonna]").
 *   A neutral citation must be used when there is one. A parallel citation follows the
 *   pinpoint ("2011 SCC 28 at para 14, [2011] 2 SCR 440").
 * - Pattern 2: a CanLII citation only when the court gave no neutral citation: style of
 *   cause, citation, pinpoint, jurisdiction and court in brackets ("1975 CanLII 148 (SCC)").
 * - Pattern 3: style of cause, year of decision in round brackets if the main citation
 *   does not show it, main citation, pinpoint "at 226" (a page in print), parallel
 *   citation, court in brackets: "Cleary v Boscowitz (1901), 8 BCR 225 at 226 (BCSC)";
 *   "R v Lyon, [1898] OJ No 129 at para 14".
 * - 2.1 statutes: italic short title, then volume and year without a comma between them,
 *   chapter and pinpoint: "Species at Risk Act, SC 2002, c 29, ss 5, 7-12"; revised volumes
 *   "RSC 1985, c C-46", "RSO 1990, c M.14"; Quebec and Manitoba continuing consolidations
 *   have no year ("CQLR c C-12").
 * - 2.5 regulations: "Canadian International Trade Tribunal Rules, SOR/91-499", consolidated
 *   federal regulations "CRC, c 879, s 2 (1978)" with the pinpoint before the revision year,
 *   Ontario "O Reg 167/07" and "RRO 1990, Reg 737, s 8(1)".
 * - 1.4 short forms: "Fisher, supra note 1 at para 30", "Income Tax Act, supra note 2,
 *   s 4(1)"; Ibid without a footnote number, repeating the previous pinpoint when none
 *   is given; supra repeats its pinpoint.
 *
 * Points the library guides do not settle, and which this generator therefore leaves to
 * the writer: how a span of pages or paragraphs is shortened (the text is kept as
 * typed), and where a judge's name falls when a parallel citation is also given (here:
 * after it).
 *
 * The page examples are checked against these functions in
 * `src/lib/__tests__/legal-styles-mcgill.test.ts`.
 */

export type McgillKind = "case" | "statute" | "regulation";

export const MCGILL_FIELDS = {
  caseName: "caseName",
  neutral: "neutral",
  canlii: "canlii",
  reporter: "reporter",
  parallel: "parallel",
  court: "court",
  paragraphs: "paragraphs",
  pages: "pages",
  judge: "judge",
  shortForm: "shortForm",
  noteNumber: "noteNumber",
  title: "title",
  series: "series",
  year: "year",
  chapter: "chapter",
  parts: "parts",
  citation: "citation",
} as const;

const F = MCGILL_FIELDS;

export interface McgillLabels {
  mcgill: string;
  footnote: string;
  later: string;
  ibid: string;
  caseNote: string;
  statuteNote: string;
  regulationNote: string;
}

/* ------------------------------------------------------------------ helpers */

const RE_PLURAL_PINPOINT = /[-–—,&]|\band\b/;
const RE_PARA_PREFIX = /^(?:paras?\.?|¶)\s*/i;
const RE_PAGE_PREFIX = /^(?:pp?\.?|pages?)\s*/i;
const RE_CHAPTER_PREFIX = /^(?:c\.?|chapter)\s+/i;
const RE_PARENS_OR_BRACKETS = /^[([](.*)[)\]]$/;
const RE_ROUND_YEAR = /^\(/;
const RE_CRC = /^CRC\b/;
const RE_REVISION_YEAR = /\(\d{4}\)\s*$/;
const RE_VERSUS = /^(.+?) v /;
const RE_CROWN = /^R v /;
const RE_RE_PREFIX = /^(Re [^(]+?)\s*\(/;

/** Continuing consolidations that carry no year: Quebec and Manitoba. */
const SERIES_WITHOUT_YEAR = new Set(["CQLR", "CCSM"]);

const STATUTE_WORDS: readonly [RegExp, string][] = [
  [/\bsections\b/gi, "ss"],
  [/\bsection\b/gi, "s"],
  [/\bschedules\b/gi, "sch"],
  [/\bschedule\b/gi, "sch"],
  [/\bparts\b/gi, "pts"],
  [/\bpart\b/gi, "pt"],
  [/\bparagraphs\b/gi, "paras"],
  [/\bparagraph\b/gi, "para"],
  [/\barticles\b/gi, "arts"],
  [/\barticle\b/gi, "art"],
  [/\bclauses\b/gi, "cls"],
  [/\bclause\b/gi, "cl"],
];

/** "section 5" → "s 5"; the Guide cites a statute's parts as s, ss, sch, pt, para, art, cl. */
export function mcgillPinpoint(input: string): string {
  let out = input.trim();
  for (const [pattern, abbreviation] of STATUTE_WORDS) {
    out = out.replace(pattern, abbreviation);
  }
  return out;
}

/** "14" → "para 14"; "14-16" or "14, 20" → "paras 14-16". */
function paragraphPinpoint(input: string): string {
  const text = input.trim().replace(RE_PARA_PREFIX, "");
  if (text === "") {
    return "";
  }
  return `${RE_PLURAL_PINPOINT.test(text) ? "paras" : "para"} ${text}`;
}

function pagePinpoint(input: string): string {
  return input.trim().replace(RE_PAGE_PREFIX, "");
}

function unwrap(text: string): string {
  const match = RE_PARENS_OR_BRACKETS.exec(text.trim());
  return match ? match[1].trim() : text.trim();
}

/**
 * The short form the Guide's examples use: the first-named party ("Radonna"), or the
 * name after "R v" ("R v Jordan" → "Jordan"). A name with no "v" is kept whole.
 */
export function mcgillShortName(name: string): string {
  const trimmed = name.trim();
  if (RE_CROWN.test(trimmed)) {
    return trimmed.replace(RE_CROWN, "");
  }
  const parties = RE_VERSUS.exec(trimmed);
  if (parties) {
    return parties[1];
  }
  return RE_RE_PREFIX.exec(trimmed)?.[1] ?? trimmed;
}

function footnoteRef(values: LegalValues): string {
  const number = get(values, F.noteNumber);
  return `note ${number || "x"}`;
}

function bracketedShort(short: string): CitationParts {
  return short === "" ? [] : [plain(" ["), italic(short), plain("]")];
}

function laterParts(
  short: string,
  values: LegalValues,
  pinpoint: string
): CitationParts {
  return [
    italic(short),
    plain(", "),
    italic("supra"),
    plain(` ${footnoteRef(values)}${pinpoint}.`),
  ];
}

function rows(
  labels: McgillLabels,
  first: CitationParts,
  later: CitationParts
): CitationOutput["entries"] {
  return [
    { label: labels.footnote, parts: tidyParts(first) },
    { label: labels.later, parts: tidyParts(later) },
    {
      label: labels.ibid,
      parts: tidyParts([italic("Ibid"), plain(".")]),
    },
  ];
}

/* -------------------------------------------------------------------- cases */

interface CaseCitation {
  main: string;
  kind: "neutral" | "canlii" | "reporter";
  parallels: string[];
}

function readCase(values: LegalValues): CaseCitation | null {
  const neutral = get(values, F.neutral);
  const canlii = get(values, F.canlii);
  const reporter = get(values, F.reporter);
  const parallel = get(values, F.parallel);
  if (neutral !== "") {
    return {
      main: neutral,
      kind: "neutral",
      parallels: [reporter, parallel].filter(Boolean),
    };
  }
  if (canlii !== "") {
    return {
      main: canlii,
      kind: "canlii",
      parallels: [reporter, parallel].filter(Boolean),
    };
  }
  if (reporter !== "") {
    return {
      main: reporter,
      kind: "reporter",
      parallels: [parallel].filter(Boolean),
    };
  }
  return null;
}

export function mcgillCaseIsReady(values: LegalValues): boolean {
  return get(values, F.caseName) !== "" && readCase(values) !== null;
}

/** Paragraphs for an online or neutral citation, pages for a printed reporter. */
function casePinpoint(values: LegalValues, kind: CaseCitation["kind"]): string {
  const paragraphs = paragraphPinpoint(get(values, F.paragraphs));
  const pages = pagePinpoint(get(values, F.pages));
  if (kind === "reporter") {
    return pages === "" ? paragraphs : pages;
  }
  return paragraphs === "" ? pages : paragraphs;
}

function formatCase(
  values: LegalValues,
  labels: McgillLabels
): CitationOutput[] {
  const citation = readCase(values);
  if (!citation) {
    return [];
  }
  const name = get(values, F.caseName);
  const pin = casePinpoint(values, citation.kind);
  const pinpointText = pin === "" ? "" : ` at ${pin}`;
  const court =
    citation.kind === "neutral"
      ? ""
      : ifPresent(unwrap(get(values, F.court)), (c) => ` (${c})`);
  const judge = ifPresent(get(values, F.judge), (j) => `, ${j}`);
  const glue = RE_ROUND_YEAR.test(citation.main) ? " " : ", ";
  const parallels = citation.parallels.map((p) => `, ${p}`).join("");
  const short = get(values, F.shortForm);
  const first: CitationParts = [
    italic(name),
    plain(`${glue}${citation.main}${pinpointText}${parallels}${court}${judge}`),
    ...bracketedShort(short),
    plain("."),
  ];
  const shortName = short || mcgillShortName(name);
  const later = laterParts(shortName, values, `${pinpointText}${judge}`);
  return [
    {
      key: "mcgill",
      label: labels.mcgill,
      entries: rows(labels, first, later),
      note: labels.caseNote,
    },
  ];
}

function ifPresent(text: string, wrap: (value: string) => string): string {
  return text === "" ? "" : wrap(text);
}

/* ----------------------------------------------------------------- statutes */

export function mcgillStatuteIsReady(values: LegalValues): boolean {
  const series = get(values, F.series);
  const needsYear = !SERIES_WITHOUT_YEAR.has(series);
  return (
    get(values, F.title) !== "" &&
    series !== "" &&
    get(values, F.chapter) !== "" &&
    (!needsYear || get(values, F.year) !== "")
  );
}

/** "SC 2002, c 29" or, for a continuing consolidation, "CQLR c C-12". */
function statuteVolume(values: LegalValues): string {
  const series = get(values, F.series);
  const chapter = get(values, F.chapter).replace(RE_CHAPTER_PREFIX, "");
  if (SERIES_WITHOUT_YEAR.has(series)) {
    return `${series} c ${chapter}`;
  }
  return `${series} ${get(values, F.year)}, c ${chapter}`;
}

function formatStatute(
  values: LegalValues,
  labels: McgillLabels
): CitationOutput[] {
  const title = get(values, F.title);
  const parts = mcgillPinpoint(get(values, F.parts));
  const pin = parts === "" ? "" : `, ${parts}`;
  const short = get(values, F.shortForm);
  const first: CitationParts = [
    italic(title),
    plain(`, ${statuteVolume(values)}${pin}`),
    ...bracketedShort(short),
    plain("."),
  ];
  const later = laterParts(short || title, values, pin);
  return [
    {
      key: "mcgill",
      label: labels.mcgill,
      entries: rows(labels, first, later),
      note: labels.statuteNote,
    },
  ];
}

/* -------------------------------------------------------------- regulations */

export function mcgillRegulationIsReady(values: LegalValues): boolean {
  return get(values, F.citation) !== "";
}

function formatRegulation(
  values: LegalValues,
  labels: McgillLabels
): CitationOutput[] {
  const title = get(values, F.title);
  const citation = get(values, F.citation);
  const parts = mcgillPinpoint(get(values, F.parts));
  const pin = parts === "" ? "" : `, ${parts}`;
  const revision =
    RE_CRC.test(citation) && !RE_REVISION_YEAR.test(citation) ? " (1978)" : "";
  const short = get(values, F.shortForm);
  const first: CitationParts = [
    ...(title === "" ? [] : [italic(title), plain(", ")]),
    plain(`${citation}${pin}${revision}`),
    ...bracketedShort(short),
    plain("."),
  ];
  const later = laterParts(short || title || citation, values, pin);
  return [
    {
      key: "mcgill",
      label: labels.mcgill,
      entries: rows(labels, first, later),
      note: labels.regulationNote,
    },
  ];
}

/* ------------------------------------------------------------------- public */

export function mcgillIsReady(kind: McgillKind, values: LegalValues): boolean {
  switch (kind) {
    case "case":
      return mcgillCaseIsReady(values);
    case "statute":
      return mcgillStatuteIsReady(values);
    default:
      return mcgillRegulationIsReady(values);
  }
}

export function formatMcgill(
  kind: McgillKind,
  values: LegalValues,
  labels: McgillLabels
): CitationOutput[] {
  switch (kind) {
    case "case":
      return formatCase(values, labels);
    case "statute":
      return formatStatute(values, labels);
    default:
      return formatRegulation(values, labels);
  }
}
